import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { ContactReferenceModel, MerchantAccount } from "../models";
import { FlexContainer, Loader } from "@mindmemobile/mm-shared-global";
import styled from "styled-components";

type PaymentFormProps = {
  onPaymentSuccess: (token: string) => void;
  onPaymentError: () => void;
  contact: ContactReferenceModel | null;
  merchantAccount: MerchantAccount;
};

const PaymentFormWrapper = styled(FlexContainer)`
  &.multiple-payment-method-form-latest {
    .payment-form {
      margin: 0 -24px;
    }
    .default-payment-form {
      margin: 0px;
    }
  }
  #ccnumber input,
  #ccexp input,
  #cvv input {
    outline: transparent solid 2px;
    outline-offset: 2px;
    min-width: 0;
    width: 100%;
    height: 40px;
    padding: 0 10px;
    background: #fff;
    border: 1px solid #aaa;
    border-radius: 3px;
    font: normal 1rem / normal inherit;
    letter-spacing: inherit;
    position: relative;
    appearance: none;
    transition: all 0.2s;
    margin-bottom: 10px;
    /* font-family: "Open Sans"; */
    font-family: "sans-serif";
    color: #555;
    &::placeholder {
      /* font-family: "Open Sans"; */
      font-family: "sans-serif";
      color: #aaa;
    }
  }
  #payButton {
    font-weight: 400;
    font-size: 16px;
    text-align: center;
    letter-spacing: normal;
    color: rgb(87, 152, 54);
    background: rgb(238, 247, 234);
    border: 1px solid transparent;
    border-radius: 4px;
    height: 30px;
    min-height: 20px;
    min-width: 85px;
    padding: 8px 15px;
    display: inline-block;
    box-sizing: border-box;
    box-shadow: none;
    outline: none;
    cursor: pointer;
    transition: 0.3s;
    line-height: 13px;
  }
  .verification-passed {
    margin-bottom: 10px;
    background-color: #eeffe5;
    padding: 4px 8px;
    border: 1px solid #579836;
    color: #579836;
    border-radius: 4px;
    font-size: 15px;
    font-weight: 600;
    width: 100%;
  }
  .verification-failed {
    margin-bottom: 10px;
    background-color: #ffdae5;
    padding: 4px 8px;
    border: 1px solid #f00;
    color: #f00;
    border-radius: 4px;
    font-size: 15px;
    font-weight: 600;
    width: 100%;
  }
`;

declare global {
  interface Window {
    CollectJS: any;
  }
}

export type NMIPaymentMethodFormHandle = {
  validateCardDetails: () => void;
};

const NMIPaymentMethodForm = forwardRef<
  NMIPaymentMethodFormHandle,
  PaymentFormProps
>(({ onPaymentSuccess, onPaymentError, contact, merchantAccount }, ref) => {
  const [isAPIInProgress, setIsAPIInProgress] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [collectJSLoaded, setCollectJSLoaded] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  // The tokenization key the CURRENT Collect.js setup belongs to. A response from a previous merchant's
  // setup (after a merchant switch) is ignored instead of being accepted as this merchant's card (F07).
  const activeTokenKeyRef = useRef<string | undefined>(merchantAccount?.collectJSKey);
  const previousTokenKeyRef = useRef<string | undefined>(merchantAccount?.collectJSKey);

  useImperativeHandle(ref, () => ({
    validateCardDetails() {
      startTokenization();
    },
  }));

  const resetForm = () => {
    setIsVerified(false);
    setErrorMessage("");
    setShowStatus(false);
  };

  // Dynamically load CollectJS
  useEffect(() => {
    // monkey patch before CollectJS loads. Applied ONCE per page: it used to wrap the already-wrapped
    // function again on every merchant change and was never restored (F07).
    if (window.customElements && !(window as any).__mmCollectJsDefinePatched) {
      (window as any).__mmCollectJsDefinePatched = true;
      const origDefine = window.customElements.define;
      window.customElements.define = (
        name: string,
        constructor: any,
        opts?: any
      ) => {
        if (
          name === "apple-spinner" ||
          name === "apple-pay-modal" ||
          window.customElements.get(name)
        ) {
          return;
        }
        return origDefine.call(window.customElements, name, constructor, opts);
      };
    }

    const tokenKey = merchantAccount?.collectJSKey;
    activeTokenKeyRef.current = tokenKey;

    // Merchant changed: a card verified for the previous merchant is not valid for this one. Drop it
    // here and in the parent (onPaymentError clears the parent's token), and stop any pending spinner.
    if (previousTokenKeyRef.current !== tokenKey) {
      previousTokenKeyRef.current = tokenKey;
      resetForm();
      setIsAPIInProgress(false);
      onPaymentError();
    }

    if (!tokenKey) return;

    // Clean up existing script + global CollectJS
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="https://secure.networkmerchants.com/token/Collect.js"]`
    );
    if (existingScript) existingScript.remove();
    if (window.CollectJS) delete window.CollectJS;

    setCollectJSLoaded(false);

    const script = document.createElement("script");
    script.src = "https://secure.networkmerchants.com/token/Collect.js";
    script.setAttribute("data-style-sniffer", "true");
    script.setAttribute("data-tokenization-key", tokenKey);

    script.onload = () => {
      setCollectJSLoaded(true);
    };

    script.onerror = () => {
      console.error("Collect.js failed to load");
      onPaymentError();
    };

    document.body.appendChild(script);

    return () => {
      // Cleanup on unmount
      if (script) script.remove();
      if (window.CollectJS) delete window.CollectJS;
      setCollectJSLoaded(false);
    };
  }, [merchantAccount?.collectJSKey]);

  // Configure CollectJS after script load
  useEffect(() => {
    if (!window.CollectJS || !collectJSLoaded) return;
    if (!merchantAccount?.collectJSKey) return;

    const configuredKey = merchantAccount?.collectJSKey;
    try {
      window.CollectJS.configure({
        variant: "inline",
        // Without a timeout, a tokenization that never answered left the form "in progress" forever.
        timeoutDuration: 20000,
        timeoutCallback: () => {
          if (activeTokenKeyRef.current !== configuredKey) return;
          setIsAPIInProgress(false);
          setShowStatus(true);
          setErrorMessage("Card check timed out. Please try again.");
        },
        styleSniffer: true,
        tokenizationKey: merchantAccount?.collectJSKey,
        fields: {
          ccnumber: {
            placeholder: "Card Number",
            selector: "#ccnumber",
          },
          ccexp: {
            placeholder: "MM/YY",
            selector: "#ccexp",
          },
          cvv: {
            placeholder: "CVV",
            selector: "#cvv",
          },
        },
        callback: (response: any) => {
          // Late answer from a previous merchant's setup: ignore it.
          if (activeTokenKeyRef.current !== configuredKey) return;
          setIsAPIInProgress(false);
          setShowStatus(true);
          if (response?.token) {
            setErrorMessage("");
            setIsVerified(true);
            onPaymentSuccess(response.token);
          } else {
            setErrorMessage("Payment method is not valid");
          }
        },
      });
    } catch (e) {
      console.error("CollectJS configuration failed", e);
      onPaymentError();
    }
  }, [merchantAccount?.collectJSKey, collectJSLoaded]);

  const handleSubmit = () => {
    if (isAPIInProgress) return;
    setErrorMessage("");
    setIsAPIInProgress(true);
    setShowStatus(false);

    try {
      startTokenization();
    } catch (e) {
      console.error("startPaymentRequest failed", e);
      setIsAPIInProgress(false);
      onPaymentError();
    }
  };

  function startTokenization() {
    const fullName = `${contact?.firstName || ""} ${contact?.lastName || ""}`;
    const email = contact?.email;
    window.CollectJS.startPaymentRequest({
      billingDetails: { name: fullName, email: email },
    });
  }

  return (
    <PaymentFormWrapper className="custom-payment-method-form">
      {!collectJSLoaded ? (
        <>{new Loader(true).loaderComponent}</>
      ) : (
        <div className="payment-form default-payment-form">
          <div id="ccnumber"></div>
          <FlexContainer spacing={10}>
            <div id="ccexp"></div>
            <div id="cvv"></div>
          </FlexContainer>
          {errorMessage && (
            <div style={{ color: "red", marginBottom: "10px" }}>
              {errorMessage}
            </div>
          )}

          {showStatus && !isVerified && (
            <div className={"verification-failed"}>
              <span> Invalid card details</span>
            </div>
          )}

          {/* <button id="payButton" onClick={handleSubmit}>
              Save
            </button> */}
        </div>
      )}
    </PaymentFormWrapper>
  );
});

export default NMIPaymentMethodForm;
